import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ImportContactsModal = ({ isOpen, onClose, onImport }) => {
  const [importStep, setImportStep] = useState('upload'); // upload, review, confirm
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [duplicateHandling, setDuplicateHandling] = useState('skip');

  // Mock imported contacts data
  const mockImportedContacts = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@email.com",
      phone: "+1 (555) 123-4567",
      isDuplicate: false,
      source: "device_contacts"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "m.chen@company.com",
      phone: "+1 (555) 234-5678",
      isDuplicate: true,
      existingGuest: "Michael Chen (m.chen@company.com)",
      source: "device_contacts"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.r@email.com",
      phone: "+1 (555) 345-6789",
      isDuplicate: false,
      source: "device_contacts"
    },
    {
      id: 4,
      name: "David Kim",
      email: "david.kim@work.com",
      phone: "+1 (555) 456-7890",
      isDuplicate: false,
      source: "device_contacts"
    },
    {
      id: 5,
      name: "Lisa Thompson",
      email: "lisa@email.com",
      phone: "+1 (555) 567-8901",
      isDuplicate: true,
      existingGuest: "Lisa Thompson (lisa.thompson@email.com)",
      source: "device_contacts"
    }
  ];

  const [importedContacts] = useState(mockImportedContacts);

  const handleFileUpload = () => {
    // Simulate file processing
    setImportStep('review');
    setSelectedContacts(importedContacts?.filter(contact => !contact?.isDuplicate)?.map(c => c?.id));
  };

  const handleContactToggle = (contactId) => {
    setSelectedContacts(prev => 
      prev?.includes(contactId) 
        ? prev?.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    const availableContacts = importedContacts?.filter(contact => 
      !contact?.isDuplicate || duplicateHandling === 'merge'
    );
    setSelectedContacts(availableContacts?.map(c => c?.id));
  };

  const handleDeselectAll = () => {
    setSelectedContacts([]);
  };

  const handleImport = () => {
    const contactsToImport = importedContacts?.filter(contact => 
      selectedContacts?.includes(contact?.id)
    );
    onImport(contactsToImport, duplicateHandling);
    onClose();
    setImportStep('upload');
    setSelectedContacts([]);
  };

  const getSelectedCount = () => {
    return selectedContacts?.length;
  };

  const getDuplicateCount = () => {
    return importedContacts?.filter(c => c?.isDuplicate)?.length;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-warm-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Import Contacts</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6">
          {/* Upload Step */}
          {importStep === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Upload" size={24} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  Import from Device Contacts
                </h3>
                <p className="text-muted-foreground mb-6">
                  Select contacts from your device to add to your guest list. We'll check for duplicates automatically.
                </p>
              </div>

              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Icon name="Smartphone" size={32} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">Access Device Contacts</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Click below to access your device contacts and import them
                </p>
                <Button
                  variant="default"
                  onClick={handleFileUpload}
                  iconName="Download"
                  iconPosition="left"
                >
                  Access Contacts
                </Button>
              </div>

              <div className="bg-muted/30 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={16} className="text-primary mt-0.5" />
                  <div className="text-sm">
                    <p className="text-foreground font-medium mb-1">Privacy Notice</p>
                    <p className="text-muted-foreground">
                      Your contacts are processed locally and only selected contacts will be saved to your guest list. 
                      We don't store or share your contact information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Review Step */}
          {importStep === 'review' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-foreground">Review Contacts</h3>
                  <p className="text-sm text-muted-foreground">
                    Found {importedContacts?.length} contacts • {getDuplicateCount()} duplicates detected
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDeselectAll}>
                    Deselect All
                  </Button>
                </div>
              </div>

              {/* Duplicate Handling */}
              <div className="bg-muted/30 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-foreground mb-3">Duplicate Handling</h4>
                <div className="space-y-2">
                  <Checkbox
                    label="Skip duplicates (recommended)"
                    checked={duplicateHandling === 'skip'}
                    onChange={() => setDuplicateHandling('skip')}
                  />
                  <Checkbox
                    label="Merge with existing guests"
                    checked={duplicateHandling === 'merge'}
                    onChange={() => setDuplicateHandling('merge')}
                  />
                </div>
              </div>

              {/* Contacts List */}
              <div className="border border-border rounded-lg">
                <div className="max-h-96 overflow-y-auto">
                  {importedContacts?.map((contact) => (
                    <div
                      key={contact?.id}
                      className={`flex items-center justify-between p-4 border-b border-border last:border-b-0 ${
                        contact?.isDuplicate && duplicateHandling === 'skip' ?'bg-muted/20 opacity-60' :''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={selectedContacts?.includes(contact?.id)}
                          onChange={() => handleContactToggle(contact?.id)}
                          disabled={contact?.isDuplicate && duplicateHandling === 'skip'}
                        />
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-foreground">
                            {contact?.name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{contact?.name}</p>
                          <p className="text-sm text-muted-foreground">{contact?.email}</p>
                          {contact?.phone && (
                            <p className="text-xs text-muted-foreground">{contact?.phone}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {contact?.isDuplicate && (
                          <div className="flex items-center space-x-1 text-warning">
                            <Icon name="AlertTriangle" size={16} />
                            <span className="text-xs font-medium">Duplicate</span>
                          </div>
                        )}
                        <Icon name="Smartphone" size={16} className="text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-foreground font-medium">
                      {getSelectedCount()} contacts selected for import
                    </span>
                    {getDuplicateCount() > 0 && (
                      <span className="text-muted-foreground ml-2">
                        • {getDuplicateCount()} duplicates {duplicateHandling === 'skip' ? 'will be skipped' : 'will be merged'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setImportStep('upload')}
                >
                  Back
                </Button>
                <Button
                  variant="default"
                  onClick={handleImport}
                  disabled={getSelectedCount() === 0}
                  iconName="UserPlus"
                  iconPosition="left"
                >
                  Import {getSelectedCount()} Contacts
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportContactsModal;